import styled from 'styled-components';

import { TitleRow } from 'components/common/accordion/styles';
import { ButtonEl } from 'components/common/button/styles';
import Container from 'components/common/container';
import { Flow } from 'components/common/flow';
import {
  DESKTOP_NAV_HEIGHT,
  MOBILE_NAV_HEIGHT,
} from 'components/common/header/constants';
import { Heading3, Heading5 } from 'components/common/text';
import { TickListEl } from 'components/common/tick-list/styles';
import { desktop, mobile, tablet } from 'utils/media';
import theme from 'utils/theme';

export const AftercareHero = styled.figure`
  position: relative;

  ${Container} {
    position: relative;
    z-index: ${theme.zIndexes.low};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding-bottom: var(--site-gutter);
    padding-top: var(--site-gutter);
    gap: var(--space-3xl);

    > svg {
      width: 520px;
      height: auto;
      max-width: calc(100vw - var(--site-gutter) * 2);
    }

    ${tablet} {
      min-height: 300px;
    }

    ${desktop} {
      min-height: 500px;
    }
  }

  :after {
    content: '';
    position: relative;
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    background: linear-gradient(
      0deg,
      rgba(0, 0, 0, 0.7) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }

  ${TickListEl} {
    color: ${theme.colors.white};

    ${tablet} {
      flex-direction: row;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
    }
  }
`;

export const AlphabetList = styled.ol`
  margin: 0 auto;
  max-width: 973px;
  list-style: none;
  padding: 0;

  > li {
    margin: 0;
  }

  > li + li {
    border-top: 1px solid ${theme.colors.modalBackgroundLight};
  }

  ol {
    display: grid;
    grid-gap: var(--site-gutter);
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    list-style: none;
    padding: 0;
    max-width: 900px;
    margin: 0 auto;

    ${mobile} {
      grid-template-columns: repeat(2, 1fr);
    }

    + * {
      margin-top: var(--space-xl-3xl);
    }
  }

  ${TitleRow} ${Heading5} {
    font-size: var(--step-4);
  }

  > :target ${TitleRow} ${Heading5} {
    color: ${theme.colors.irisPurple};
  }
`;

interface ILetterLinkProps {
  selected: boolean;
}

export const AlphabetNav = styled.nav`
  display: block;
  text-align: center;
  overflow-x: auto;
  scrollbar-width: none;
  white-space: nowrap;
  height: var(--space-4xl);
  margin-bottom: var(--space-s-l);
  margin-top: calc(-1 * var(--space-l));
  padding: var(--space-s) 0;
  z-index: ${theme.zIndexes.mid};
  background: ${theme.colors.offWhite};
  position: sticky;
  top: ${MOBILE_NAV_HEIGHT}px;

  ${desktop} {
    top: ${DESKTOP_NAV_HEIGHT}px;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  ::-webkit-scrollbar-track {
    background-color: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: transparent;
  }
`;

export const LetterLink = styled.a<ILetterLinkProps>`
  --background-scale: ${({ selected }) => (selected ? 1 : 0)};

  color: ${theme.colors.midGrey};
  font-family: ${theme.fonts.heading};
  position: relative;
  font-size: var(--step-1);
  width: var(--space-xl);
  height: var(--space-xl);
  display: inline-flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  transition: ${theme.transitionSpeeds.fast}s color;
  flex-shrink: 0;

  & + & {
    margin-left: var(--space-3xs);

    :before {
      content: '•';
      position: absolute;
      left: calc(-1 * var(--space-xs));
      top: 50%;
      transform: translateY(-50%) translateX(40%) scale(0.8);
      color: ${theme.colors.black};
      transition: ${theme.transitionSpeeds.fast}s opacity;
    }
  }

  :after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    transform: scale(var(--background-scale));
    background-color: ${theme.colors.black};
    height: var(--space-xl);
    width: var(--space-xl);
    border-radius: 100%;
    z-index: ${theme.zIndexes.low};
    transition: ${theme.transitionSpeeds.fast}s transform;
  }

  &[href] {
    color: ${({ selected }) => theme.colors[selected ? 'white' : 'black']};

    :hover {
      color: ${theme.colors.white};
      --background-scale: 1;
    }
  }

  span {
    position: relative;
    z-index: ${theme.zIndexes.mid};
    top: 0.1em; // Optical adjustment
  }

  &:before,
  & + :before {
    ${({ selected }) => selected && 'opacity: 0;'}
  }

  &[href]:hover:before,
  &[href]:hover + :before {
    opacity: 0;
  }
`;

interface IAftercareCtaProps {
  colorScheme?: Sproutl.ColorScheme;
}

export const AftercareCta = styled.article<IAftercareCtaProps>`
  position: relative;
  padding: var(--site-gutter);
  border-radius: ${theme.radii.default};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  color: ${({ colorScheme }) => theme.colors[colorScheme || 'white']};
  overflow: hidden;
  margin: var(--space-l) 0 var(--space-xl-3xl);

  ${Heading3} {
    font-size: var(--step-5);
  }

  ${Flow} {
    z-index: ${theme.zIndexes.low};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    min-height: 200px;

    ${tablet} {
      text-align: center;
      min-height: 340px;
      align-items: center;
    }
  }

  ${ButtonEl} {
    position: static;

    :active {
      transform: none;
    }

    :after {
      content: '';
      position: absolute;
      z-index: ${theme.zIndexes.mid};
      inset: 0;
    }
  }
`;
